import React from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { Column, Row, Text, Icon, Card } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import checkLargeTokens from "@amzn/meridian-tokens/base/icon/check-large";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL } from "src/constants/constants";
import { useAppDispatch } from "src/store/store";
import { resetInspectionCaptureState } from "src/store/inspectionCaptureSlice";
import PillButton from "src/components/UI/PillButton";
import "./inspectionReviewPage.scss";

/**
 * Router state passed by InspectionReviewPage after a successful submit.
 * - overallCompliant: whether every scored question came back compliant.
 * - isAustinLinked: whether the inspection was linked to an Austin scheduled
 *   inspection (austinInspectionId present) — findings/actions are only created
 *   in Austin for an Austin-linked, non-compliant inspection.
 */
export interface InspectionSubmittedState {
    overallCompliant: boolean;
    isAustinLinked: boolean;
}

const InspectionSubmittedPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { state } = useLocation();
    const submittedState = state as InspectionSubmittedState | null;

    // Reached without submit context (e.g. a page refresh or direct navigation) —
    // there is nothing to confirm, so send the user back to the dashboard.
    if (!submittedState) {
        return <Navigate to="/home" replace />;
    }

    const { overallCompliant, isAustinLinked } = submittedState;
    // The "Austin action and findings created" message only applies when Austin
    // actually created findings, i.e. an Austin-linked inspection that was non-compliant.
    const austinFindingsCreated = isAustinLinked && !overallCompliant;

    const goToDashboard = () => {
        dispatch(resetInspectionCaptureState());
        navigate("/home");
    };

    return (
        <div className="inspection-review-page pwa-scrollable">
            <Column spacingInset="600 500" spacing="400">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="inspection-review-back-button"
                        onClick={goToDashboard}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="primary" />
                    </div>
                    <Text type="b300" color="primary">
                        Submit Inspection
                    </Text>
                </Row>

                {austinFindingsCreated && (
                    <Row spacing="300" alignmentVertical="center">
                        <Icon tokens={checkLargeTokens} color="success" />
                        <Text type="h200" color="success">
                            Austin action and findings created successfully
                        </Text>
                    </Row>
                )}

                <Card spacingInset="none">
                    <Column spacingInset="400" spacing="200">
                        <Text type="h200" color="primary">
                            Inspection Summary
                        </Text>
                        <Text type="b100" color="primary">
                            OBSERVATION
                        </Text>
                        <Text
                            type="h300"
                            color={overallCompliant ? "success" : "error"}
                        >
                            {overallCompliant ? COMPLIANT_LABEL : NON_COMPLIANT_LABEL}
                        </Text>
                    </Column>
                </Card>

                <PillButton text="BACK TO DASHBOARD" color="secondary" onClick={goToDashboard} />
            </Column>
        </div>
    );
};

export default InspectionSubmittedPage;
